import { Injectable, computed, inject, signal } from "@angular/core";
import type {
    IUserNotificationPreferenceDto,
    IUserPreferenceDto,
    IUserPreferenceItemDto,
    IUserPreferenceResponse,
} from "../../interfaces";
import DataService from "../../Common/services/data.service";
import ToastService from "../../Common/services/toast.service";

const EMPTY_PREFERENCES: IUserPreferenceDto = {
    CreateUserID: "",
    HomeRoute: "/",
    Timezone: "America/New_York",
    ColorScheme: "system",
    GridDensity: "compact",
    DefaultDateRange: "last30",
    AutoEnableHistorical: false,
    NotificationsMaster: true,
    DoNotDisturb: false,
    DndFrom: "20:00:00",
    DndTo: "07:00:00",
    AfterDnd: "digest",
};

const EMPTY_RESPONSE: IUserPreferenceResponse = { Preferences: EMPTY_PREFERENCES, Notifications: [], Items: [] };

export type ResetScope = "all" | "preferences" | "notifications" | "items";

@Injectable({
    providedIn: "root",
})
export class UserPreferenceService {
    private readonly dataService = inject(DataService);
    private readonly toastr = inject(ToastService);

    private readonly state = signal<IUserPreferenceResponse>(EMPTY_RESPONSE);
    readonly loaded = signal(false);

    readonly preferences = computed(() => this.state().Preferences);
    readonly notifications = computed(() => this.state().Notifications);
    readonly fieldDefaults = computed(() => this.itemsMap("FieldDefault"));
    readonly defaultTabs = computed(() => this.itemsMap("DefaultTab"));

    /** The request currently in flight, if any — lets a resolver join a hover-triggered prefetch instead of duplicating it. */
    private inflight: Promise<IUserPreferenceResponse> | null = null;
    private writes: Promise<unknown> = Promise.resolve();

    /** Preferences as they currently stand, fetching them first only if they have never been loaded. */
    load(): Promise<IUserPreferenceResponse> {
        return this.loaded() ? Promise.resolve(this.state()) : this.refresh();
    }

    /**
     * Fetches the latest preferences from the server. If a request is already in flight
     * (e.g. a hover-triggered preload), joins it instead of firing a duplicate — otherwise
     * starts a fresh one, guaranteeing up-to-date data.
     */
    refresh(): Promise<IUserPreferenceResponse> {
        return (this.inflight ??= this.fetch().finally(() => (this.inflight = null)));
    }

    /**
     * Resolves with the user's preferences once they have actually been fetched. Consumers that run
     * during app bootstrap — e.g. a grid reacting to URL query params on a deep link — would otherwise
     * read the EMPTY_PREFERENCES placeholder and behave as though every preference were switched off.
     */
    whenLoaded(): Promise<IUserPreferenceDto> {
        // A failed fetch falls back to the placeholder defaults rather than blocking the caller;
        // the request itself is already reported by the HTTP error interceptor.
        return this.load().then(
            (result) => result.Preferences,
            () => this.preferences()
        );
    }

    private fetch(): Promise<IUserPreferenceResponse> {
        return this.dataService.getApi<IUserPreferenceResponse>("UserPreference").then((result) => {
            this.state.set(result);
            this.loaded.set(true);
            return result;
        });
    }

    savePreferences(patch: Partial<IUserPreferenceDto>): void {
        this.enqueueWrite("There was an error saving your preferences.", () => {
            const updated = { ...this.state().Preferences, ...patch };
            return this.dataService
                .putApi<IUserPreferenceDto, IUserPreferenceDto>("UserPreference", updated)
                .then((result) => this.state.update((current) => ({ ...current, Preferences: result })));
        });
    }

    saveNotificationRow(code: string, patch: Partial<IUserNotificationPreferenceDto>): void {
        this.enqueueWrite("There was an error saving your notification preferences.", () => {
            const rows = this.state().Notifications;
            const exists = rows.some((row) => row.Code === code);
            const updatedRows = exists
                ? rows.map((row) => (row.Code === code ? { ...row, ...patch } : row))
                : [...rows, { Code: code, Title: code, InApp: true, Email: false, Sms: false, ...patch }];
            return this.dataService
                .putApi<
                    IUserNotificationPreferenceDto[],
                    IUserNotificationPreferenceDto[]
                >("UserPreference/notifications", updatedRows)
                .then((result) => this.state.update((current) => ({ ...current, Notifications: result })));
        });
    }

    saveItem(category: string, key: string, value: string): void {
        this.enqueueWrite("There was an error saving your defaults.", () => {
            const items = this.state().Items.filter((item) => item.Category === category);
            const exists = items.some((item) => item.Key === key);
            const updatedItems: IUserPreferenceItemDto[] = exists
                ? items.map((item) => (item.Key === key ? { ...item, Value: value } : item))
                : [...items, { Category: category, Key: key, Value: value }];
            return this.dataService
                .putApi<
                    IUserPreferenceItemDto[],
                    IUserPreferenceItemDto[]
                >(`UserPreference/items?category=${category}`, updatedItems)
                .then((result) =>
                    this.state.update((current) => ({
                        ...current,
                        Items: [...current.Items.filter((item) => item.Category !== category), ...result],
                    }))
                );
        });
    }

    reset(scope: ResetScope = "all", category?: string): void {
        this.enqueueWrite("There was an error resetting your preferences.", () => {
            const query = category ? `scope=${scope}&category=${category}` : `scope=${scope}`;
            return this.dataService
                .postApi<IUserPreferenceResponse, object>(`UserPreference/reset?${query}`, {})
                .then((result) => this.state.set(result));
        });
    }

    private enqueueWrite(errorMessage: string, run: () => Promise<unknown>): void {
        this.writes = this.writes.then(run).catch(() => this.toastr.error(errorMessage));
    }

    private itemsMap(category: string): Record<string, string> {
        const map: Record<string, string> = {};
        this.state()
            .Items.filter((item) => item.Category === category)
            .forEach((item) => (map[item.Key] = item.Value));
        return map;
    }
}
